import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Button, Dialog, AppBar, Toolbar, IconButton, Typography, Slide,
  useMediaQuery, Stack, TextField, Autocomplete, Chip, Alert,
  LinearProgress, Tabs, Tab, Divider, Badge,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import ThermostatIcon from "@mui/icons-material/Thermostat";
import { useTheme } from "@mui/material/styles";
import {
  ResponsiveContainer, BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, Tooltip, CartesianGrid, Cell, ReferenceLine,
} from "recharts";

/* ---------- ערים ---------- */
const CITIES = [
  { label: "שדרות", lat: 31.521, lon: 34.595 },
  { label: "תל אביב", lat: 32.0853, lon: 34.7818 },
  { label: "ירושלים", lat: 31.7683, lon: 35.2137 },
  { label: "באר שבע", lat: 31.2529, lon: 34.7915 },
  { label: "חיפה", lat: 32.794, lon: 34.9896 },
  { label: "אשדוד", lat: 31.8014, lon: 34.6435 },
];

const CATEGORIES = [
  { label: "ללא עומס", color: "#22a06b", min: -Infinity },
  { label: "קל", color: "#6bbf59", min: 22 },
  { label: "מתון", color: "#e6b422", min: 24, dark: true },
  { label: "בינוני", color: "#e67e22", min: 26, dark: true },
  { label: "כבד", color: "#d35400", min: 28 },
  { label: "קיצוני", color: "#c0392b", min: 30 },
];

const CHART = {
  grid: "rgba(15, 23, 42, 0.06)",
  axis: "#94a3b8",
  tick: "#64748b",
  areaStroke: "#ea580c",
};

/* ---------- נוסחת DI וקטגוריות ---------- */
function calcDI(tC, rhPct) {
  const rh = Math.max(0, Math.min(100, Number(rhPct)));
  return tC - 0.55 * (1 - rh / 100) * (tC - 14.5);
}
function diCategory(di) {
  if (di < 22) return CATEGORIES[0];
  if (di < 24) return CATEGORIES[1];
  if (di < 26) return CATEGORIES[2];
  if (di < 28) return CATEGORIES[3];
  if (di < 30) return CATEGORIES[4];
  return CATEGORIES[5];
}

function formatDayLabel(date, opts) {
  return new Date(date).toLocaleDateString("he-IL", opts);
}

/* ---------- API ---------- */
async function fetchHourly(lat, lon) {
  const url = new URL("https://api.open-meteo.com/v1/forecast");
  url.searchParams.set("latitude", String(lat));
  url.searchParams.set("longitude", String(lon));
  url.searchParams.set("hourly", "temperature_2m,relative_humidity_2m");
  url.searchParams.set("forecast_days", "7");
  url.searchParams.set("timezone", "auto");
  const res = await fetch(url.toString());
  if (!res.ok) throw new Error("Weather fetch failed");
  return res.json();
}

async function fetchCurrent(lat, lon) {
  const url = new URL("https://api.open-meteo.com/v1/forecast");
  url.searchParams.set("latitude", String(lat));
  url.searchParams.set("longitude", String(lon));
  url.searchParams.set("current", "temperature_2m,relative_humidity_2m");
  url.searchParams.set("timezone", "auto");
  const res = await fetch(url.toString());
  if (!res.ok) throw new Error("Current weather fetch failed");
  const data = await res.json();
  const t = data?.current?.temperature_2m;
  const rh = data?.current?.relative_humidity_2m;
  if (typeof t !== "number" || typeof rh !== "number") {
    throw new Error("נתונים נוכחיים חסרים");
  }
  const di = Number(calcDI(t, rh).toFixed(1));
  const cat = diCategory(di);
  return {
    di,
    t,
    rh,
    cat,
    lat,
    lon,
    updatedAt: data?.current?.time ? new Date(data.current.time) : new Date(),
  };
}

function getCurrentPosition() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("הדפדפן לא תומך במיקום."));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        resolve({
          lat: +pos.coords.latitude.toFixed(4),
          lon: +pos.coords.longitude.toFixed(4),
        }),
      (e) => reject(new Error("לא ניתן לקבל מיקום: " + e.message)),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  });
}

function transform(data) {
  const hours = data?.hourly?.time ?? [];
  const temps = data?.hourly?.temperature_2m ?? [];
  const rhs = data?.hourly?.relative_humidity_2m ?? [];

  const byDate = {};
  for (let i = 0; i < hours.length; i++) {
    const ts = hours[i];
    if (!ts) continue;
    const t = temps[i];
    const rh = rhs[i];
    if (typeof t !== "number" || typeof rh !== "number") continue;

    const [date, hhmm] = ts.split("T");
    const hour = Number((hhmm || "00").slice(0, 2));
    const di = calcDI(t, rh);

    if (!byDate[date]) byDate[date] = { max: -999, hour: 0, t, rh, list: [] };
    byDate[date].list.push({ hour, t, rh, di: Number(di.toFixed(1)) });
    if (di > byDate[date].max) {
      byDate[date].max = di;
      byDate[date].hour = hour;
      byDate[date].t = t;
      byDate[date].rh = rh;
    }
  }

  return Object.entries(byDate)
    .sort(([a], [b]) => (a < b ? -1 : 1))
    .map(([date, d]) => {
      const cat = diCategory(d.max);
      return {
        date,
        maxDI: Number(d.max.toFixed(1)),
        maxHour: d.hour,
        t: d.t,
        rh: d.rh,
        color: cat.color,
        label: cat.label,
        dark: !!cat.dark,
        hours: d.list.sort((a, b) => a.hour - b.hour),
      };
    });
}

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

function ChartTooltip({ active, payload, label, mode }) {
  if (!active || !payload?.length) return null;
  const di = payload[0].value;
  const cat = diCategory(di);
  return (
    <Box
      sx={{
        bgcolor: "#0f172a",
        color: "#fff",
        px: 1.5,
        py: 1,
        borderRadius: 2,
        boxShadow: "0 8px 24px rgba(15,23,42,0.25)",
        minWidth: 120,
        direction: "rtl",
      }}
    >
      <Typography sx={{ fontSize: 12, color: "#94a3b8", mb: 0.3 }}>
        {mode === "hour" ? `שעה ${label}:00` : label}
      </Typography>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: cat.color, flexShrink: 0 }} />
        <Typography sx={{ fontWeight: 800, fontSize: 14 }}>
          יא״נ {di}
        </Typography>
      </Stack>
      <Typography sx={{ fontSize: 12, color: cat.color, fontWeight: 700, mt: 0.2 }}>
        {cat.label}
      </Typography>
    </Box>
  );
}

function CurrentDiHero({ live, loading, error, onRefresh }) {
  const cat = live?.cat;
  const textColor = cat?.dark ? "#0f172a" : "#fff";

  return (
    <Box
      sx={{
        mb: 2,
        p: 2,
        borderRadius: 3,
        background: cat
          ? `linear-gradient(135deg, ${cat.color} 0%, ${cat.color}cc 100%)`
          : "linear-gradient(135deg, #64748b 0%, #475569 100%)",
        color: textColor,
        boxShadow: cat ? `0 8px 24px ${cat.color}40` : "0 8px 24px rgba(15,23,42,0.15)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
        <Box>
          <Typography sx={{ fontSize: 12, fontWeight: 800, opacity: 0.85, letterSpacing: "0.04em" }}>
            יא״נ נוכחי · {live?.fromFallback ? "מיקום נבחר" : "מיקום שלך"}
          </Typography>
          {live?.updatedAt && (
            <Typography sx={{ fontSize: 11, opacity: 0.75, mt: 0.2 }}>
              עודכן{" "}
              {live.updatedAt.toLocaleTimeString("he-IL", { hour: "2-digit", minute: "2-digit" })}
            </Typography>
          )}
        </Box>
        <IconButton
          size="small"
          onClick={onRefresh}
          disabled={loading}
          aria-label="רענון"
          sx={{ color: textColor, opacity: 0.85, "&:hover": { opacity: 1, bgcolor: "rgba(255,255,255,0.15)" } }}
        >
          <MyLocationIcon fontSize="small" />
        </IconButton>
      </Stack>

      {loading && !live && (
        <Box sx={{ py: 1.5 }}>
          <LinearProgress
            sx={{
              borderRadius: 99,
              height: 4,
              bgcolor: "rgba(255,255,255,0.25)",
              "& .MuiLinearProgress-bar": { bgcolor: "#fff" },
            }}
          />
          <Typography sx={{ fontSize: 13, mt: 1, opacity: 0.9 }}>מאתר מיקום ונתונים בזמן אמת…</Typography>
        </Box>
      )}

      {error && !live && (
        <Typography sx={{ fontSize: 13, fontWeight: 600, py: 1 }}>{error}</Typography>
      )}

      {live && (
        <Stack direction="row" alignItems="flex-end" justifyContent="space-between" mt={0.5}>
          <Box>
            <Typography
              sx={{
                fontWeight: 900,
                fontSize: 48,
                lineHeight: 1,
                letterSpacing: "-0.03em",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {live.di.toFixed(1)}
            </Typography>
            <Typography sx={{ fontWeight: 800, fontSize: 16, mt: 0.5 }}>{cat.label}</Typography>
          </Box>
          <Box sx={{ textAlign: "left", pb: 0.5 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, opacity: 0.95 }}>
              {live.t.toFixed(1)}°C
            </Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 600, opacity: 0.85 }}>
              לחות {Math.round(live.rh)}%
            </Typography>
            <Typography sx={{ fontSize: 10, opacity: 0.7, mt: 0.3, fontVariantNumeric: "tabular-nums" }}>
              {live.lat}, {live.lon}
            </Typography>
          </Box>
        </Stack>
      )}

      {loading && live && (
        <LinearProgress
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            height: 3,
            bgcolor: "transparent",
            "& .MuiLinearProgress-bar": { bgcolor: "rgba(255,255,255,0.7)" },
          }}
        />
      )}
    </Box>
  );
}

function DayRow({ day, selected, onClick }) {
  return (
    <Box
      onClick={onClick}
      role={onClick ? "button" : undefined}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        p: 1.25,
        pr: 1.5,
        borderRadius: 2.5,
        bgcolor: selected ? "rgba(234, 88, 12, 0.08)" : "#fff",
        border: "1px solid",
        borderColor: selected ? "rgba(234, 88, 12, 0.35)" : "rgba(15, 23, 42, 0.06)",
        cursor: onClick ? "pointer" : "default",
        transition: "border-color 0.2s, background-color 0.2s, transform 0.15s",
        "&:hover": onClick
          ? { borderColor: "rgba(234, 88, 12, 0.4)", transform: "translateY(-1px)" }
          : undefined,
      }}
    >
      <Box
        sx={{
          width: 5,
          alignSelf: "stretch",
          borderRadius: 99,
          bgcolor: day.color,
          flexShrink: 0,
        }}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontWeight: 800, fontSize: 14, color: "#0f172a", lineHeight: 1.3 }}>
          {formatDayLabel(day.date, { weekday: "long", day: "2-digit", month: "2-digit" })}
        </Typography>
        <Typography sx={{ fontSize: 12, color: "#64748b", mt: 0.2 }}>
          שיא {day.maxHour}:00 · {day.t.toFixed(0)}° · לחות {Math.round(day.rh)}%
        </Typography>
      </Box>
      <Box sx={{ textAlign: "left", flexShrink: 0 }}>
        <Typography sx={{ fontWeight: 900, fontSize: 18, color: day.color, lineHeight: 1 }}>
          {day.maxDI.toFixed(1)}
        </Typography>
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: day.color, mt: 0.2 }}>
          {day.label}
        </Typography>
      </Box>
    </Box>
  );
}

export default function HeatStressPopupHourly({ open: externalOpen, onClose: externalClose } = {}) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const controlled = externalOpen !== undefined;
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlled ? externalOpen : internalOpen;
  const setOpen = controlled ? () => {} : setInternalOpen;

  const [tab, setTab] = useState(0);
  const [city, setCity] = useState(CITIES[0]);
  const [lat, setLat] = useState(CITIES[0].lat);
  const [lon, setLon] = useState(CITIES[0].lon);

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [days, setDays] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);

  const [live, setLive] = useState(null);
  const [liveLoading, setLiveLoading] = useState(false);
  const [liveErr, setLiveErr] = useState(null);

  const canFetch = useMemo(
    () => typeof lat === "number" && typeof lon === "number" && !Number.isNaN(lat) && !Number.isNaN(lon),
    [lat, lon]
  );

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    controlled ? externalClose() : setOpen(false);
  };

  const loadLiveDi = async () => {
    setLiveLoading(true);
    setLiveErr(null);
    try {
      const pos = await getCurrentPosition();
      setCity(null);
      setLat(pos.lat);
      setLon(pos.lon);
      const current = await fetchCurrent(pos.lat, pos.lon);
      setLive(current);
    } catch (e) {
      const msg = e?.message || "שגיאה בטעינת יא״נ נוכחי";
      if (canFetch) {
        try {
          const current = await fetchCurrent(lat, lon);
          setLive({ ...current, fromFallback: true });
          setLiveErr(null);
        } catch {
          setLiveErr(msg);
        }
      } else {
        setLiveErr(msg);
      }
    } finally {
      setLiveLoading(false);
    }
  };

  const useGPS = () => {
    setErr(null);
    loadLiveDi();
  };

  const fetchData = async () => {
    if (!canFetch) return;
    try {
      setLoading(true);
      setErr(null);
      const data = await fetchHourly(lat, lon);
      const d = transform(data);
      setDays(d);
      setSelectedDate(d[0]?.date || null);
    } catch (e) {
      setErr(e?.message || "שגיאה בטעינת תחזית");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && canFetch) fetchData();
  }, [open, lat, lon]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    loadLiveDi();
    const id = setInterval(loadLiveDi, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedDay = days.find((d) => d.date === selectedDate);

  const dailyChartData = useMemo(
    () =>
      days.map((d) => ({
        name: formatDayLabel(d.date, { weekday: "short", day: "2-digit" }),
        di: d.maxDI,
        color: d.color,
        date: d.date,
      })),
    [days]
  );

  const hourlyChartData = useMemo(() => {
    if (!selectedDay) return [];
    return selectedDay.hours.map((h) => ({
      hour: h.hour,
      di: h.di,
      t: h.t,
      rh: h.rh,
      color: diCategory(h.di).color,
    }));
  }, [selectedDay]);

  const yDomain = useMemo(() => {
    const values = tab === 0
      ? days.map((d) => d.maxDI)
      : (selectedDay?.hours.map((h) => h.di) ?? []);
    if (!values.length) return [18, 34];
    const min = Math.min(...values);
    const max = Math.max(...values);
    return [Math.max(16, Math.floor(min - 2)), Math.min(36, Math.ceil(max + 2))];
  }, [tab, days, selectedDay]);

  return (
    <>
      {!controlled && (
        <Badge
          badgeContent="חדש!"
          color="warning"
          overlap="circular"
          anchorOrigin={{ vertical: "top", horizontal: "left" }}
          sx={{
            "& .MuiBadge-badge": {
              left: 5,
              top: 1,
              bgcolor: "#ff6d00",
              color: "#fff",
              borderRadius: "12px",
              padding: "2px 2px",
              fontWeight: 100,
              height: "25px",
              width: "50px",
              maxWidth: "70px",
            },
          }}
        >
          <Button variant="contained" fullWidth onClick={handleOpen}>
            תחזית עומס חום
          </Button>
        </Badge>
      )}

      <Dialog
        open={open}
        onClose={handleClose}
        TransitionComponent={Transition}
        fullScreen={fullScreen}
        PaperProps={{
          sx: {
            width: fullScreen ? "100%" : 460,
            maxHeight: fullScreen ? "100%" : "92vh",
            m: fullScreen ? 0 : 2,
            borderRadius: fullScreen ? 0 : 3,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          },
        }}
      >
        <AppBar
          position="relative"
          elevation={0}
          sx={{
            background: "linear-gradient(135deg, #ea580c 0%, #c2410c 55%, #9a3412 100%)",
            boxShadow: "none",
            flexShrink: 0,
          }}
        >
          <Toolbar sx={{ minHeight: 56 }}>
            <IconButton edge="start" color="inherit" onClick={handleClose} aria-label="סגור">
              <CloseIcon />
            </IconButton>
            <ThermostatIcon sx={{ ml: 0.5, opacity: 0.9 }} />
            <Typography sx={{ ml: 1, flex: 1, fontWeight: 800, letterSpacing: "-0.01em" }}>
              תחזית עומס חום
            </Typography>
            <Typography sx={{ fontSize: 12, opacity: 0.85, fontWeight: 600 }}>
              7 ימים
            </Typography>
          </Toolbar>
        </AppBar>

        <Box
          sx={{
            p: 2,
            background: "linear-gradient(180deg, #fff7ed 0%, #f8fafc 28%, #f8fafc 100%)",
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
          }}
          dir="rtl"
        >
          <CurrentDiHero
            live={live}
            loading={liveLoading}
            error={liveErr}
            onRefresh={loadLiveDi}
          />

          <Stack spacing={1.5} mb={1.5}>
            <Autocomplete
              options={CITIES}
              value={city}
              onChange={(_, v) => {
                setCity(v);
                if (v) {
                  setLat(v.lat);
                  setLon(v.lon);
                }
              }}
              getOptionLabel={(o) => o?.label ?? ""}
              renderInput={(p) => (
                <TextField
                  {...p}
                  label="בחר עיר"
                  size="small"
                  sx={{
                    bgcolor: "#fff",
                    borderRadius: 2,
                    "& .MuiOutlinedInput-root": { borderRadius: 2 },
                  }}
                />
              )}
            />
            <Stack direction="row" spacing={1}>
              <TextField
                label="רוחב"
                size="small"
                type="number"
                fullWidth
                value={lat}
                onChange={(e) => setLat(e.target.value === "" ? "" : Number(e.target.value))}
                inputProps={{ step: "0.0001", inputMode: "decimal" }}
                sx={{ bgcolor: "#fff", "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
              <TextField
                label="אורך"
                size="small"
                type="number"
                fullWidth
                value={lon}
                onChange={(e) => setLon(e.target.value === "" ? "" : Number(e.target.value))}
                inputProps={{ step: "0.0001", inputMode: "decimal" }}
                sx={{ bgcolor: "#fff", "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
            </Stack>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                onClick={useGPS}
                fullWidth
                startIcon={<MyLocationIcon />}
                sx={{
                  borderRadius: 2,
                  borderColor: "rgba(234, 88, 12, 0.4)",
                  color: "#c2410c",
                  fontWeight: 700,
                  bgcolor: "#fff",
                  "&:hover": { borderColor: "#ea580c", bgcolor: "#fff7ed" },
                }}
              >
                המיקום שלי
              </Button>
              <Button
                variant="contained"
                onClick={fetchData}
                disabled={!canFetch}
                fullWidth
                sx={{
                  borderRadius: 2,
                  fontWeight: 800,
                  bgcolor: "#ea580c",
                  boxShadow: "0 4px 14px rgba(234, 88, 12, 0.35)",
                  "&:hover": { bgcolor: "#c2410c" },
                }}
              >
                חשב
              </Button>
            </Stack>

            <Stack direction="row" spacing={0.75} sx={{ overflowX: "auto", pb: 0.5 }}>
              {CATEGORIES.map((c) => (
                <Chip
                  key={c.label}
                  size="small"
                  label={c.label}
                  icon={
                    <Box
                      component="span"
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor: c.color,
                        ml: "8px !important",
                        mr: "-4px !important",
                      }}
                    />
                  }
                  sx={{
                    flexShrink: 0,
                    bgcolor: "#fff",
                    border: "1px solid rgba(15,23,42,0.08)",
                    fontWeight: 700,
                    fontSize: 11,
                    color: "#334155",
                    "& .MuiChip-icon": { margin: 0 },
                  }}
                />
              ))}
            </Stack>
          </Stack>

          {loading && (
            <LinearProgress
              sx={{
                mb: 1.5,
                borderRadius: 99,
                height: 4,
                bgcolor: "rgba(234, 88, 12, 0.12)",
                "& .MuiLinearProgress-bar": { bgcolor: "#ea580c" },
              }}
            />
          )}
          {err && (
            <Alert severity="error" sx={{ my: 1, borderRadius: 2 }}>
              {err}
            </Alert>
          )}

          {!loading && days.length > 0 && (
            <>
              <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                variant="fullWidth"
                sx={{
                  mb: 1.5,
                  minHeight: 40,
                  bgcolor: "rgba(15, 23, 42, 0.04)",
                  borderRadius: 2.5,
                  p: 0.4,
                  "& .MuiTabs-indicator": {
                    height: "100%",
                    borderRadius: 2,
                    bgcolor: "#fff",
                    boxShadow: "0 1px 4px rgba(15,23,42,0.1)",
                    zIndex: 0,
                  },
                  "& .MuiTab-root": {
                    minHeight: 36,
                    zIndex: 1,
                    fontWeight: 800,
                    fontSize: 13,
                    color: "#64748b",
                    textTransform: "none",
                    "&.Mui-selected": { color: "#c2410c" },
                  },
                }}
              >
                <Tab label="לפי ימים" disableRipple />
                <Tab label="לפי שעות" disableRipple />
              </Tabs>

              {tab === 0 && (
                <>
                  <Box
                    sx={{
                      height: 280,
                      mb: 2,
                      p: 1.5,
                      pt: 2,
                      bgcolor: "#fff",
                      borderRadius: 3,
                      border: "1px solid rgba(15, 23, 42, 0.06)",
                      boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
                    }}
                  >
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#94a3b8", mb: 1, px: 0.5 }}>
                      שיא יא״נ לפי יום
                    </Typography>
                    <ResponsiveContainer width="100%" height="88%">
                      <BarChart
                        data={dailyChartData}
                        margin={{ left: -8, right: 8, top: 8, bottom: 0 }}
                        onClick={(state) => {
                          const date = state?.activePayload?.[0]?.payload?.date;
                          if (date) {
                            setSelectedDate(date);
                            setTab(1);
                          }
                        }}
                        style={{ cursor: "pointer" }}
                      >
                        <defs>
                          {days.map((d, i) => (
                            <linearGradient key={d.date} id={`barGrad-${i}`} x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor={d.color} stopOpacity={1} />
                              <stop offset="100%" stopColor={d.color} stopOpacity={0.65} />
                            </linearGradient>
                          ))}
                        </defs>
                        <CartesianGrid strokeDasharray="4 6" stroke={CHART.grid} vertical={false} />
                        <XAxis
                          dataKey="name"
                          tick={{ fill: CHART.tick, fontSize: 11, fontWeight: 600 }}
                          axisLine={false}
                          tickLine={false}
                        />
                        <YAxis
                          domain={yDomain}
                          tick={{ fill: CHART.axis, fontSize: 11 }}
                          axisLine={false}
                          tickLine={false}
                          width={32}
                        />
                        <Tooltip
                          cursor={{ fill: "rgba(234, 88, 12, 0.06)" }}
                          content={<ChartTooltip mode="day" />}
                        />
                        <ReferenceLine y={28} stroke="#d35400" strokeDasharray="4 4" strokeOpacity={0.45} />
                        <Bar dataKey="di" radius={[10, 10, 4, 4]} maxBarSize={36}>
                          {days.map((d, i) => (
                            <Cell key={d.date} fill={`url(#barGrad-${i})`} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>

                  <Stack spacing={1}>
                    {days.map((d) => (
                      <DayRow
                        key={d.date}
                        day={d}
                        onClick={() => {
                          setSelectedDate(d.date);
                          setTab(1);
                        }}
                      />
                    ))}
                  </Stack>
                </>
              )}

              {tab === 1 && (
                <>
                  <Box sx={{ display: "flex", overflowX: "auto", gap: 0.8, mb: 1.5, pb: 0.5 }}>
                    {days.map((d) => {
                      const active = selectedDate === d.date;
                      return (
                        <Chip
                          key={d.date}
                          label={formatDayLabel(d.date, { weekday: "short", day: "2-digit", month: "2-digit" })}
                          onClick={() => setSelectedDate(d.date)}
                          sx={{
                            flexShrink: 0,
                            fontWeight: 800,
                            borderRadius: 2,
                            bgcolor: active ? d.color : "#fff",
                            color: active ? (d.dark ? "#0f172a" : "#fff") : "#334155",
                            border: active ? "none" : "1px solid rgba(15,23,42,0.08)",
                            boxShadow: active ? `0 4px 12px ${d.color}55` : "none",
                            transition: "transform 0.15s, box-shadow 0.15s",
                            "&:hover": { bgcolor: active ? d.color : "#fff7ed" },
                          }}
                        />
                      );
                    })}
                  </Box>

                  {selectedDay && (
                    <Box
                      sx={{
                        height: 280,
                        mb: 2,
                        p: 1.5,
                        pt: 2,
                        bgcolor: "#fff",
                        borderRadius: 3,
                        border: "1px solid rgba(15, 23, 42, 0.06)",
                        boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
                      }}
                    >
                      <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#94a3b8", mb: 1, px: 0.5 }}>
                        יא״נ לפי שעה ·{" "}
                        {formatDayLabel(selectedDay.date, { weekday: "long", day: "2-digit", month: "2-digit" })}
                      </Typography>
                      <ResponsiveContainer width="100%" height="88%">
                        <AreaChart
                          data={hourlyChartData}
                          margin={{ left: -8, right: 8, top: 8, bottom: 0 }}
                        >
                          <defs>
                            <linearGradient id="hourlyArea" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor={CHART.areaStroke} stopOpacity={0.35} />
                              <stop offset="85%" stopColor={CHART.areaStroke} stopOpacity={0.02} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="4 6" stroke={CHART.grid} vertical={false} />
                          <XAxis
                            dataKey="hour"
                            tick={{ fill: CHART.tick, fontSize: 10, fontWeight: 600 }}
                            axisLine={false}
                            tickLine={false}
                            interval={2}
                            tickFormatter={(h) => `${h}`}
                          />
                          <YAxis
                            domain={yDomain}
                            tick={{ fill: CHART.axis, fontSize: 11 }}
                            axisLine={false}
                            tickLine={false}
                            width={32}
                          />
                          <Tooltip content={<ChartTooltip mode="hour" />} />
                          <ReferenceLine y={28} stroke="#d35400" strokeDasharray="4 4" strokeOpacity={0.45} />
                          <ReferenceLine y={22} stroke="#22a06b" strokeDasharray="4 4" strokeOpacity={0.35} />
                          <Area
                            type="monotone"
                            dataKey="di"
                            stroke={CHART.areaStroke}
                            strokeWidth={2.5}
                            fill="url(#hourlyArea)"
                            dot={false}
                            activeDot={{
                              r: 5,
                              strokeWidth: 2,
                              stroke: "#fff",
                              fill: CHART.areaStroke,
                            }}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </Box>
                  )}

                  {selectedDay && (
                    <Stack spacing={0.75}>
                      {selectedDay.hours
                        .filter((h) => h.hour % 2 === 0 || h.di === selectedDay.maxDI)
                        .map((h) => {
                          const cat = diCategory(h.di);
                          const isPeak = h.hour === selectedDay.maxHour;
                          return (
                            <Box
                              key={h.hour}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1.25,
                                px: 1.25,
                                py: 0.9,
                                borderRadius: 2,
                                bgcolor: isPeak ? "rgba(234, 88, 12, 0.08)" : "#fff",
                                border: "1px solid",
                                borderColor: isPeak
                                  ? "rgba(234, 88, 12, 0.3)"
                                  : "rgba(15, 23, 42, 0.05)",
                              }}
                            >
                              <Box
                                sx={{
                                  width: 4,
                                  height: 28,
                                  borderRadius: 99,
                                  bgcolor: cat.color,
                                  flexShrink: 0,
                                }}
                              />
                              <Typography sx={{ fontWeight: 800, fontSize: 13, color: "#0f172a", width: 42 }}>
                                {String(h.hour).padStart(2, "0")}:00
                              </Typography>
                              <Typography sx={{ flex: 1, fontSize: 12, color: "#64748b" }}>
                                {h.t.toFixed(1)}° · לחות {Math.round(h.rh)}%
                                {isPeak ? " · שיא" : ""}
                              </Typography>
                              <Typography sx={{ fontWeight: 900, fontSize: 14, color: cat.color }}>
                                {h.di.toFixed(1)}
                              </Typography>
                              <Typography sx={{ fontSize: 11, fontWeight: 700, color: cat.color, minWidth: 48, textAlign: "left" }}>
                                {cat.label}
                              </Typography>
                            </Box>
                          );
                        })}
                    </Stack>
                  )}
                </>
              )}

              <Divider sx={{ my: 1.5, borderColor: "rgba(15,23,42,0.06)" }} />
              <Typography variant="caption" sx={{ color: "#94a3b8", display: "block", lineHeight: 1.6 }}>
                יא״נ = T − 0.55·(1 − RH/100)·(T − 14.5). יומי = שיא היום. קו מקווקו ב־28 = עומס כבד.
                {tab === 0 ? " לחיצה על יום פותחת פירוט שעתי." : ""}
              </Typography>
            </>
          )}

          {!loading && !err && days.length === 0 && (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              בחר עיר/קואורדינטות, או לחץ GPS ולאחר מכן ״חשב״.
            </Alert>
          )}
        </Box>
      </Dialog>
    </>
  );
}
